import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { observer } from 'mobx-react';

import { FileList, UploadFileButton } from 'components/file-uploader';
import UploadedFileList from 'components/file-uploader/UploadedFileList';
import usePublicApi from 'public-api';
import UploadedFile from 'stores/UploadedFile';
import { Divider, Form } from 'ui';

export type FileUploadHandler = {
  handleFileUpload: (file: File) => void;
};

type Props = {
  render?: ({
    button,
    fileList,
  }: {
    button: React.ReactNode;
    fileList: React.ReactNode;
  }) => React.ReactElement<any, any> | null;
  value: string[];
  size?: React.ComponentProps<typeof UploadFileButton>['size'];
  onChange: (value: string[]) => void;
  setIsLoading?: (isLoading: boolean) => void;
  buttonClassNames?: (classes: string[]) => string[];
  fileListClassNames?: (classes: string[]) => string[];
} & Omit<React.ComponentProps<typeof Form.Input>, 'onChange' | 'value'>;

const FileUploadField = forwardRef(
  (
    { label, onChange, value, error, setIsLoading, render, size, buttonClassNames, fileListClassNames }: Props,
    ref: ForwardedRef<FileUploadHandler | undefined>,
  ) => {
    buttonClassNames = buttonClassNames ?? ((a) => a);
    fileListClassNames = fileListClassNames ?? ((a) => a);

    const publicApi = usePublicApi();
    const [uploadingFiles, setUploadingFiles] = useState<IUploadedFile[]>([]);

    // This state is used as a queue before file upload ids will be sent to onChange
    const [fileUploadIdsQueue, setFileUploadIdsQueue] = useState<UUIDField[]>([]);

    useEffect(() => {
      if (fileUploadIdsQueue.length > 0) {
        onChange([...(value ?? []), ...fileUploadIdsQueue]);
        setFileUploadIdsQueue([]);
      }
    }, [fileUploadIdsQueue]);

    const onFileUploaded = (fileUpload: BackendPublic.FileUpload, uploadedFile: IUploadedFile) => {
      setUploadingFiles((old) => old.filter((item) => item.id !== uploadedFile.id));
      setFileUploadIdsQueue((old) => [...old, fileUpload.id]);
    };

    const handleFileUpload = async (file: File) => {
      const uploadedFile = new UploadedFile({
        api: publicApi,
        file,
        onUploaded: onFileUploaded,
      });
      uploadedFile.upload();
      setUploadingFiles((old) => [...old, uploadedFile]);
    };

    useImperativeHandle(ref, () => ({
      handleFileUpload(file: File) {
        handleFileUpload(file);
      },
    }));

    const handleUploadingFileRemove = (file: IUploadedFile) => {
      file.abort();
      setUploadingFiles((old) => old.filter((item) => item.id !== file.id));
    };

    useEffect(() => {
      if (setIsLoading) {
        setIsLoading(uploadingFiles.length > 0);
      }
    }, [uploadingFiles]);

    if (render) {
      return render({
        fileList: (
          <>
            <FileList
              files={value}
              onRemove={(removedId) => onChange(value.filter((fileId: UUIDField) => fileId !== removedId))}
              classNames={fileListClassNames}
            />
            {uploadingFiles?.length > 0 && value?.length > 0 && <Divider />}
            <UploadedFileList files={uploadingFiles} onRemove={handleUploadingFileRemove} />
          </>
        ),
        button: (
          <UploadFileButton classNames={buttonClassNames} size={size} uploadFile={handleFileUpload} error={!!error}>
            {label ? <strong>{label}</strong> : null}
          </UploadFileButton>
        ),
      });
    }

    return (
      <Form.Field error={error}>
        <div className="w-full space-y-2">
          <FileList
            files={value}
            onRemove={(removedId) => onChange(value.filter((fileId: UUIDField) => fileId !== removedId))}
            classNames={fileListClassNames}
          />
          {uploadingFiles?.length > 0 && value?.length > 0 && <Divider />}
          <UploadedFileList files={uploadingFiles} onRemove={handleUploadingFileRemove} />
          <UploadFileButton classNames={buttonClassNames} size={size} uploadFile={handleFileUpload} error={!!error}>
            {label ? <strong>{label}</strong> : null}
          </UploadFileButton>
        </div>
      </Form.Field>
    );
  },
);

export default observer(FileUploadField);
